// ServiceHero.js
"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "../Styles/Servicehero.css";

import digitalImg from "../Assets/bg11.jpg";
import brandingImg from "../Assets/bg6.jpg";
import webImg from "../Assets/bg13.jpg";
import mobileImg from "../Assets/bg10.jpg";
import uxImg from "../Assets/bg20.avif";
import adsImg from "../Assets/bg21.avif";
import smImg from "../Assets/bg19.jpg";
const ServiceHeroSection = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const calculateAndUpdateTimelineHeight = () => {
      if (timelineRef.current) {
        const newHeight = timelineRef.current.getBoundingClientRect().height;
        setTimelineHeight((prevHeight) =>
          newHeight > 0 && newHeight !== prevHeight ? newHeight : prevHeight
        );
      }
    };

    const handleLayoutReady = () => {
      calculateAndUpdateTimelineHeight();
      AOS.refresh();
    };

    const timer = setTimeout(calculateAndUpdateTimelineHeight, 50);

    if (document.readyState === "complete") {
      const readyTimer = setTimeout(handleLayoutReady, 100);
      return () => clearTimeout(readyTimer);
    } else {
      window.addEventListener("load", handleLayoutReady);
    }

    window.addEventListener("resize", handleLayoutReady);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("load", handleLayoutReady);
      window.removeEventListener("resize", handleLayoutReady);
    };
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, timelineHeight]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="service-hero-container" ref={containerRef}>
      {/* Header */}
      <div className="service-hero-header">
        <h2 className="service-hero-title">Our Marketing Services</h2>
        <p className="service-hero-description">
          We help brands grow with innovative marketing strategies, impactful digital campaigns,
          and data-driven solutions tailored to achieve real results.
        </p>
      </div>

      {/* Timeline */}
      <div ref={timelineRef} className="service-hero-timeline">
        {data.map((item, index) => (
          <div key={index} className="service-hero-step">
            {/* Marker */}
            <div className="service-hero-sticky">
              <div className="service-hero-marker-outer">
                <div className="service-hero-marker-inner" />
              </div>
              <h3 className="service-hero-step-title-desktop">{item.title}</h3>
            </div>

            {/* Content */}
            <div className="service-hero-step-content">
              <h3 className="service-hero-step-title-mobile">{item.title}</h3>

              {/* Image */}
              <img
                src={item.image}
                alt={item.title}
                className="service-hero-step-image"
                data-aos="fade-left"
              />

              {/* Description */}
              <div className="service-hero-step-paragraph" data-aos="fade-left">
                <p>{item.description}</p>

                {/* Services List */}
                {item.servicesTitle && <h4>{item.servicesTitle}</h4>}
                {Array.isArray(item.services) &&
                  item.services.map((service, idx) => (
                    <p key={idx} className="service-hero-step-point">
                      <span className="service-hero-arrow-icon">→</span> {service}
                    </p>
                  ))}
              </div>
            </div>
          </div>
        ))}

        {/* Progress Bar */}
        <div
          style={{ height: `${timelineHeight}px` }}
          className="service-hero-progress-bar-wrapper"
        >
          <motion.div
            style={{ height: heightTransform, opacity: opacityTransform }}
            className="service-hero-progress-bar"
          />
        </div>
      </div>
    </div>
  );
};

const Servicehero = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const timelineData = [
    {
      title: "Digital Marketing",
      image: digitalImg,
      description:
        "Developing a long-term and reliable marketing strategy that ensures customer loyalty while driving measurable growth.",
      servicesTitle: "Our Digital Marketing Services",
      services: [
        "Inbound Marketing",
        "SEO",
        "SEM & PPC",
        "Social Media Optimization",
        "Analytics Consultation",
        "Online Reputation Management",
        "Content Marketing",
        "Email Marketing",
      ],
    },
    {
      title: "Branding",
      image: brandingImg,
      description:
        "Let your brand do the talking. Crafting a unique identity that connects with your audience and builds trust.",
      servicesTitle: "Our Brand Identity Solutions",
      services: [
        "Logo & Brand Identity",
        "Graphic Design",
        "Explainer Videos",
        "Infographics Design",
        "Print Design",
        "Creative Content Writing",
      ],
    },
    {
      title: "Web Development",
      image: webImg,
      description:
        "From simple websites to advanced web apps, we deliver responsive, scalable, and business-focused web solutions.",
      servicesTitle: "Our Web Development Services",
      services: [
        "Custom Application Development",
        "Content Management System (CMS)",
        "Ecommerce Development",
        "MEAN Stack Development",
        "Social Media Apps",
      ],
    },
    {
      title: "Mobile",
      image: mobileImg,
      description:
        "We design and develop mobile apps with intuitive UI/UX that deliver seamless experiences across devices.",
      servicesTitle: "Our Mobile App Solutions",
      services: [
        "Android App Development",
        "React Native App Development",
        "iPhone App Development",
        "iPad App Development",
        "Apple Watch App Development",
        "Windows Mobile App Development",
        "PhoneGap App Development",
        "Xamarin App Development",
      ],
    },
    {
      title: "UX Design",
      image: uxImg,
      description:
        "Our UX experts design user journeys that balance usability, functionality, and business goals.",
      servicesTitle: "Our UX Design Services",
      services: [
        "UX Research",
        "Information Architecture",
        "Mood Boards",
        "Wireframing",
        "Interaction Design",
        "UX Testing",
      ],
    },
    {
  title: "Ads Promotion",
  image: adsImg, // make sure you import the correct image for Ads Promotion
  description:
    "Effective ad campaigns across platforms that maximize reach, engagement, and conversions for your business.",
  servicesTitle: "Our Ads Promotion Services",
  services: [
    "Google Ads",
    "Facebook Ads",
    "Instagram Ads",
    "LinkedIn Ads",
    "YouTube Ads",
    "Display Advertising",
  ],
},
{
  title: "Social Media Marketing",
  image: smImg,
  description:
    "Build your brand presence and engagement on social media platforms with tailored campaigns that convert followers into customers.",
  servicesTitle: "Our Social Media Marketing Services",
  services: [
    "Content Strategy",
    "Social Media Ads",
    "Community Management",
    "Influencer Marketing",
    "Social Media Analytics",
    "Campaign Optimization",
  ],
},


  ];

  return <ServiceHeroSection data={timelineData} />;
};

export default Servicehero;
